import type React from 'react';
import type { ReactNode, RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { hasActiveOverlays } from '../FullscreenOverlay/overlayStack';

type PanelContent = React.ComponentType<{ config?: unknown }>;
type PanelProvider = React.ComponentType<{ children: ReactNode }>;

type OpenPanelOptions = {
  provider?: PanelProvider;
  config?: unknown;
  stacked?: boolean;
};

type SavedPanel = {
  panelId: string;
  content: PanelContent;
  provider: PanelProvider | null;
  config: unknown;
};

type DetailPanelContextValue = {
  activePanelId: string | null;
  panelConfig: unknown;
  activeContent: PanelContent | null;
  activeProvider: PanelProvider | null;
  openPanel: (panelId: string, content: PanelContent, options?: OpenPanelOptions) => void;
  closePanel: () => void;
  isMobile: boolean;
  closeLabel?: string;
  closeLabelShort?: string;
};

const DetailPanelContext = createContext<DetailPanelContextValue | null>(null);

type DetailPanelProviderProps = {
  children: ReactNode;
  isMobile: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  closeLabel?: string;
  closeLabelShort?: string;
};

export const DetailPanelProvider = ({
  children,
  isMobile,
  scrollContainerRef,
  closeLabel,
  closeLabelShort,
}: DetailPanelProviderProps) => {
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [panelConfig, setPanelConfig] = useState<unknown>(undefined);
  const [activeContent, setActiveContent] = useState<PanelContent | null>(null);
  const [activeProvider, setActiveProvider] = useState<PanelProvider | null>(null);
  const savedScrollPositionRef = useRef(0);
  const parentPanelRef = useRef<SavedPanel | null>(null);

  const restoreParentPanel = useCallback(() => {
    const parent = parentPanelRef.current;
    parentPanelRef.current = null;

    if (parent) {
      setActivePanelId(parent.panelId);
      setPanelConfig(parent.config);
      setActiveContent(() => parent.content);
      setActiveProvider(() => parent.provider);
    } else {
      setActivePanelId(null);
      setPanelConfig(undefined);
      setActiveContent(null);
      setActiveProvider(null);

      if (!isMobile && scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = savedScrollPositionRef.current;
      }
    }
  }, [isMobile, scrollContainerRef]);

  const closePanel = useCallback(() => {
    restoreParentPanel();
  }, [restoreParentPanel]);

  const openPanel = useCallback(
    (panelId: string, content: PanelContent, options?: OpenPanelOptions) => {
      if (panelId === activePanelId) {
        restoreParentPanel();
        return;
      }

      if (options?.stacked && activePanelId && activeContent) {
        parentPanelRef.current = {
          panelId: activePanelId,
          content: activeContent,
          provider: activeProvider,
          config: panelConfig,
        };
      } else {
        parentPanelRef.current = null;
      }

      setPanelConfig(options?.config);
      setActiveContent(() => content);
      setActiveProvider(() => options?.provider ?? null);
      setActivePanelId(panelId);

      if (!isMobile && scrollContainerRef.current) {
        savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }
    },
    [isMobile, scrollContainerRef, activePanelId, activeContent, activeProvider, panelConfig, restoreParentPanel],
  );

  useEffect(() => {
    if (isMobile || !activePanelId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !hasActiveOverlays()) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, activePanelId, closePanel]);

  return (
    <DetailPanelContext.Provider
      value={{
        activePanelId,
        panelConfig,
        activeContent,
        activeProvider,
        openPanel,
        closePanel,
        isMobile,
        closeLabel,
        closeLabelShort,
      }}
    >
      {children}
    </DetailPanelContext.Provider>
  );
};

export const useDetailPanel = () => {
  const context = useContext(DetailPanelContext);
  if (!context) {
    throw new Error('useDetailPanel must be used within a DetailPanelProvider');
  }
  return context;
};

export const useDetailPanelSafe = () => useContext(DetailPanelContext);
